import * as React from 'react'
import { Form as FinalForm } from 'react-final-form'
import { useMutation } from '@apollo/react-hooks'
import { up } from 'styled-breakpoints'
import styled from 'styled-components'
import { useFeature } from 'toggled'

import BirthdateField from '~/components/birthdate-field'
import TextField from '~/components/text-field'
import { updateUserMutation } from '~/gql/mutations'
import { useFormatters } from '~/hooks'
import type { ModalManagerProps } from '~/hooks/use-modal-manager'
import useVendor from '~/hooks/use-vendor'
import Anchor from '~/truck/anchor'
import Button from '~/truck/button'
import Field from '~/truck/field'
import type { IUser } from '~/types/users'
import { handleSubmissionErrors } from '~/utils'
import { flags } from '~/utils/constants'

import Modal from '../modal'

import { ActionsContainer, TOCContainer } from './elements'
import { getFormInitialValues, getSubmitPayload, getSupportedCustomFieldConfigs, onValidateFormValues } from './utils'

const StyledFormContainer = styled.div`
  display: grid;
  gap: ${props => props.theme.spacing.comfortable}px;

  ${up('lg')} {
    grid-template-columns: 1fr 1fr;
  }
`

interface NeedsToBeUpdatedProps extends ModalManagerProps {
  user: IUser
}

const NeedsToBeUpdated = (props: NeedsToBeUpdatedProps) => {
  const { user, modalId } = props

  const vendor = useVendor()

  const [updateUser] = useMutation(updateUserMutation)

  const formatters = useFormatters()

  const { name: vendorName, legalTermsUrl, privacyPolicyUrl } = vendor

  const initialValues = React.useMemo(() => {
    return getFormInitialValues(user, vendor.settings)
  }, [user, vendor.settings])

  const formFields = React.useMemo(() => {
    return getSupportedCustomFieldConfigs(vendor.settings)
  }, [vendor.settings])

  const onSubmit = async values => {
    try {
      const payload = await getSubmitPayload(values, vendor.settings)

      await updateUser({ variables: payload })

      window.scrollTo(0, 0)
    } catch (error) {
      return handleSubmissionErrors(error)
    }
  }

  const allowAdvertisingFF = useFeature(flags.ALLOW_ADVERTISING)

  const renderContent = formProps => {
    const canSubmit =
      (!legalTermsUrl || formProps.values.acceptedTermsAndConditions) &&
      (!privacyPolicyUrl || formProps.values.acceptedPrivacyPolicy)

    const hasCheckBoxes = legalTermsUrl || privacyPolicyUrl || !!allowAdvertisingFF

    return (
      <>
        <div style={{ overflowY: 'auto' }}>
          <StyledFormContainer>
            {formFields.map(field => {
              if (field.name === 'birthdate') {
                return (
                  <div key={field.name}>
                    <BirthdateField />
                  </div>
                )
              }

              return (
                <div key={field.name}>
                  <TextField
                    as="input"
                    type={field.type}
                    label={field.label}
                    placeholder={field.placeholder}
                    name={field.name}
                    parse={formatters[field.formatterName]}
                  />
                </div>
              )
            })}
          </StyledFormContainer>
          {hasCheckBoxes && (
            <TOCContainer>
              {legalTermsUrl && (
                <Field as="checkbox" name="acceptedTermsAndConditions" hasText textStyle="h6Regular">
                  {'Declaro que he leído y acepto los '}
                  <Anchor href="/terminos-y-condiciones" target="_blank" $textStyle="h6Regular" $color="info">
                    Términos y Condiciones
                  </Anchor>
                  {` de ${vendorName}`}
                </Field>
              )}
              {privacyPolicyUrl && (
                <Field as="checkbox" name="acceptedPrivacyPolicy" hasText textStyle="h6Regular">
                  {'Declaro que he leído y acepto la '}
                  <Anchor href="/politica-de-privacidad" target="_blank" $textStyle="h6Regular" $color="info">
                    Política de Privacidad
                  </Anchor>
                  {` de ${vendorName}`}
                </Field>
              )}
              {allowAdvertisingFF ? (
                <Field as="checkbox" name="allowAdvertising" hasText textStyle="h6Regular">
                  {allowAdvertisingFF.settings?.message
                    ? allowAdvertisingFF.settings.message
                    : 'Brindo mi consentimiento para recibir publicidad sobre ofertas,' +
                      ' promociones e información sobre productos de la tienda.'}
                </Field>
              ) : null}
            </TOCContainer>
          )}
        </div>
        <ActionsContainer>
          <Button
            disabled={!canSubmit}
            loading={formProps.submitting}
            onClick={formProps.handleSubmit}
            color="secondary"
          >
            Guardar y Continuar
          </Button>
        </ActionsContainer>
      </>
    )
  }

  return (
    <Modal>
      <>
        <Modal.Header modalId={modalId} title="Bienvenido, actualiza tus datos por favor" />
        <FinalForm
          initialValues={initialValues}
          onSubmit={onSubmit}
          validate={formValues => onValidateFormValues(formValues, vendor.settings)}
        >
          {renderContent}
        </FinalForm>
      </>
    </Modal>
  )
}

export default NeedsToBeUpdated
